import { Form, Modal } from "antd"
import React, { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  buildReportSettingsPatch,
  reportSettingsFormValues,
  type ReportSettingsPatch,
  type ReportSettingsSource,
  type ReportSettingsValues
} from "../helpers/reportSettings.ts"
import { ReportGeneralFields } from "./ReportGeneralFields.tsx"

interface ReportSettingsModalProps {
  report: ReportSettingsSource
  folderOptions: string[]
  onSubmit: (patch: ReportSettingsPatch) => Promise<boolean>
  onClose: () => void
  /** Beside the folder picker; handed the setter that files the report. */
  renderFolderAction?: (setFolder: (folder: string) => void) => ReactNode
}

export const ReportSettingsModal = ({
  report,
  folderOptions,
  onSubmit,
  onClose,
  renderFolderAction
}: ReportSettingsModalProps) => {
  const { t } = useTranslation()
  const [values, setValues] = useState<ReportSettingsValues>(() =>
    reportSettingsFormValues(report)
  )
  const [nameError, setNameError] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleChange = (patch: Partial<ReportSettingsValues>) => {
    setValues((prev) => ({ ...prev, ...patch }))
    if (patch.name !== undefined) setNameError(false)
  }

  // Stay open when the save is rejected so the typed values survive a retry.
  const handleOk = async () => {
    if (!values.name.trim()) {
      setNameError(true)
      return
    }
    const patch = buildReportSettingsPatch(report, values)
    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      if (await onSubmit(patch)) onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      maskClosable={false}
      title={t("reports.settingsModal.title")}
      okText={t("reports.settingsModal.save")}
      cancelText={t("reports.cancel")}
      confirmLoading={saving}
      onCancel={onClose}
      onOk={handleOk}
    >
      <Form layout="vertical" className="with-default-error">
        <ReportGeneralFields
          autoFocusName
          value={values}
          onChange={handleChange}
          folderOptions={folderOptions}
          nameError={nameError}
          folderAction={renderFolderAction?.((folder) =>
            handleChange({ folder })
          )}
        />
      </Form>
    </Modal>
  )
}
